import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge, Button, Spinner } from '@atlas/ui-primitives'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Fragment } from 'react'
import { EventPayloadDetail } from '@/components/audit/EventPayloadDetail'
import { formatUtc } from '@/lib/format-utc'
import type { EventDTO } from '@/services/activity.service'

/**
 * The audit log table. Presentational and prop-driven, so it is storiable and the
 * routed page owns every fetch.
 *
 * A row expands to the entry's payload as recorded, which is the point of the
 * page: the columns say what happened to what, the payload says what changed.
 */

export interface AuditTableProps {
	rows: EventDTO[]
	isLoading?: boolean
	isError?: boolean
	/** Which row is expanded (`eventId`), or null. */
	expandedId: string | null
	onToggleExpanded: (eventId: string) => void
}

/** Kept next to the header row: the expanded detail must span every column. */
const COLUMN_COUNT = 7

/** Nothing was recorded in that cell, rendered so it cannot be read as a value. */
const EMPTY = '--'

export function AuditTable({ rows, isLoading, isError, expandedId, onToggleExpanded }: AuditTableProps) {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner size="lg" />
			</div>
		)
	}

	if (isError) {
		return (
			<p role="alert" className="py-8 text-center text-ds-danger text-sm">
				Could not load the audit log. Please try again.
			</p>
		)
	}

	if (rows.length === 0) {
		return <EmptyState title="No audit entries match these filters." variant="filtered" />
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>{/* expand toggle */}</TableHead>
					<TableHead>When (UTC)</TableHead>
					<TableHead>Entity</TableHead>
					<TableHead>Entity id</TableHead>
					<TableHead>Event</TableHead>
					<TableHead>Source</TableHead>
					<TableHead>Actor</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map((row) => (
					<Fragment key={row.eventId}>
						<TableRow className="cursor-pointer" onClick={() => onToggleExpanded(row.eventId)}>
							<TableCell>
								{/* A real button, not just a clickable row: a `role="button"` on a
								    `<tr>` fights its own `row` role, and without this the payload -
								    the point of the page - is unreachable by keyboard. The wrapper
								    stops the row's own click from toggling twice; a PressEvent has
								    no stopPropagation, so the DOM event is contained here. */}
								<span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
									<Button
										variant="ghost"
										size="sm"
										aria-expanded={expandedId === row.eventId}
										aria-controls={`audit-row-${row.eventId}`}
										aria-label={`${expandedId === row.eventId ? 'Collapse' : 'Expand'} event ${row.type}`}
										onPress={() => onToggleExpanded(row.eventId)}
									>
										{expandedId === row.eventId ? (
											<ChevronDown className="h-4 w-4" aria-hidden="true" />
										) : (
											<ChevronRight className="h-4 w-4" aria-hidden="true" />
										)}
									</Button>
								</span>
							</TableCell>
							<TableCell className="font-mono text-xs">{formatUtc(row.timestamp)}</TableCell>
							<TableCell>
								{row.entityType ? (
									<Badge variant="muted" size="sm">
										{row.entityType}
									</Badge>
								) : (
									<span className="text-ds-text-muted">{EMPTY}</span>
								)}
							</TableCell>
							<TableCell className="max-w-xs truncate font-mono text-xs" title={row.entityId}>
								{row.entityId ?? <span className="text-ds-text-muted">{EMPTY}</span>}
							</TableCell>
							<TableCell className="font-mono text-xs">{row.type}</TableCell>
							<TableCell className="text-ds-text-subtle text-sm">{row.source}</TableCell>
							<TableCell className="max-w-xs truncate font-mono text-xs" title={row.userId}>
								{row.userId ?? <span className="text-ds-text-muted">{EMPTY}</span>}
							</TableCell>
						</TableRow>
						{expandedId === row.eventId ? (
							<TableRow>
								<TableCell id={`audit-row-${row.eventId}`} colSpan={COLUMN_COUNT} className="bg-ds-surface">
									{/* The actor as the id that was recorded, not a name: resolving names
									    needs `GET /users`, which is SUPER_ADMIN-only, and this page is
									    open to ADMIN. A 403 for the role the page exists for is worse
									    than a UUID. */}
									<EventPayloadDetail event={row} actorName={row.userId ?? EMPTY} />
								</TableCell>
							</TableRow>
						) : null}
					</Fragment>
				))}
			</TableBody>
		</Table>
	)
}
